import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import API from 'api/API';
import { DraftSatoSetResponse, OnboardingStatus } from 'api/Responses/SatoResponses';
import { useLanguage } from 'context/AppContext';
import { SelectedSetSelectors } from 'context/SelectedSet';
import { useSelectedSet } from 'context/SelectedSet/SelectedSetContext';
import { getSetData } from 'utils/helpers/SATO.helpers';

interface HookReturn {
  loading: boolean;
  set: DraftSatoSetResponse;
  rejectSet: () => Promise<void>;
  approveSet: () => Promise<void>;
  updateSetData: () => Promise<void>;
}

const useReviewSet = (drafts: DraftSatoSetResponse[], setCode: string): HookReturn | undefined => {
  const [language] = useLanguage();
  const { selectedSetState, dispatchSelectedSet } = useSelectedSet();
  const [loading, setLoading] = useState(false);

  // Used more specifically for on page refresh when no set is declared in the reducer. This will make an API call and pass the correct data into the reducer.
  useEffect(() => {
    if (!selectedSetState.selectedSet) {
      const draftSet = getSetData(drafts, setCode);
      if (draftSet) {
        dispatchSelectedSet({
          type: 'SET_SELECTED_SET',
          payload: draftSet,
        });
      }
    }
  }, [dispatchSelectedSet, drafts, selectedSetState, setCode]);

  const set = useMemo(
    () => (selectedSetState.selectedSet ? SelectedSetSelectors.selectSelectedSet(selectedSetState) : undefined),
    [selectedSetState],
  );

  // Check if exists
  if (!set) {
    return;
  }

  const rejectSet = async (): Promise<void> => {
    setLoading(true);

    try {
      await API.sato.updateSetStatus(set.code, OnboardingStatus.REJECTED);

      dispatchSelectedSet({
        type: 'REVIEW_SET',
        payload: { status: OnboardingStatus.REJECTED },
      });

      toast.success(language.Sato.updateStatus.setRejected);
    } catch (error) {
      API.errorHandler(error);
    }

    setLoading(false);
  };

  // Split approval out from rejection because it could handle submitting the set to the ML pipeline in future
  const approveSet = async (): Promise<void> => {
    setLoading(true);

    try {
      await API.sato.updateSetStatus(set.code, OnboardingStatus.APPROVED);

      dispatchSelectedSet({
        type: 'REVIEW_SET',
        payload: { status: OnboardingStatus.APPROVED },
      });

      toast.success(language.Sato.updateStatus.setApproved);
    } catch (error) {
      API.errorHandler(error);
    }

    setLoading(false);
  };

  const updateSetData = async (): Promise<void> => {
    setLoading(true);

    try {
      const updated_set = await API.sato.updateSetData(set.code);

      dispatchSelectedSet({
        type: 'SET_SELECTED_SET',
        payload: updated_set,
      });

      toast.success(language.Sato.updateStatus.setUpdated);
    } catch (error) {
      API.errorHandler(error);
    }

    setLoading(false);
  };

  return { set, loading, rejectSet, approveSet, updateSetData };
};

export default useReviewSet;
